/**
 * The one status chip, used everywhere Netly Pay shows a state.
 *
 * Each chip carries a dot SHAPE as well as a colour, per the design brief:
 * overdue is a square, due a hollow ring, partly paid a half-filled circle.
 * Colour alone would leave "paid" and "overdue" indistinguishable to a
 * red-green colour-blind owner reading a column of fees, which is the one
 * column where that matters.
 *
 * The labels, tones and shapes live in NetlyPayManager so the chip and the
 * filter counts cannot drift apart.
 */
import React from 'react';
import {
  CHIP_CLASSES,
  memberChip,
  requestChip,
  type Chip,
  type ChipDot,
  type MemberStatus,
  type RequestStatus,
} from '../../../internals/managers/NetlyPayManager';

const Dot = ({shape}: {shape: ChipDot}) => {
  const base = 'inline-block flex-shrink-0 w-[7px] h-[7px] current';
  switch (shape) {
    case 'solid':
      return <span className={`${base} rounded-full bg-current`} />;
    case 'ring':
      return <span className={`${base} rounded-full border-[1.5px] border-current`} />;
    case 'square':
      return <span className={`${base} rounded-[2px] bg-current`} />;
    case 'hollow-square':
      return <span className={`${base} rounded-[2px] border-[1.5px] border-current`} />;
    case 'half':
      // Half-filled: a ring with the left half inked.
      return (
        <span className={`${base} rounded-full border-[1.5px] border-current overflow-hidden`}>
          <span className="block w-1/2 h-full bg-current" />
        </span>
      );
    case 'ring-dot':
      return (
        <span
          className={`${base} rounded-full border-[1.5px] border-current flex items-center justify-center`}
        >
          <span className="block w-[2px] h-[2px] rounded-full bg-current" />
        </span>
      );
    case 'bar':
      return <span className="inline-block flex-shrink-0 w-[9px] h-[2px] bg-current" />;
    default:
      return <span className={`${base} rounded-full bg-current`} />;
  }
};

export const ChipTag = ({chip, className = ''}: {chip: Chip; className?: string}) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
      CHIP_CLASSES[chip.tone]
    } ${className}`}
  >
    <Dot shape={chip.dot} />
    {chip.label}
  </span>
);

export const RequestStatusChip = ({status}: {status: RequestStatus}) => (
  <ChipTag chip={requestChip(status)} />
);

export const MemberStatusChip = ({status}: {status: MemberStatus}) => (
  <ChipTag chip={memberChip(status)} />
);

export default ChipTag;
